import { DictatedCourse } from '../../contexts/NewTeacherProvider';

export type InstitutionType = 'university' | 'languages' | 'schools';

export interface Institution {
  external_id: string;
  name: string;
  type?: InstitutionType;
}

export interface Course {
  external_id: string;
  name: string;
  institution: Institution;
}

export interface BusinessHours {
  startTime: string;
  endTime: string;
}

export interface Configuration {
  in_person_classes: boolean;
  in_person_classes_message: string;
  virtual_classes: boolean;
  virtual_classes_message: string;
  weeks_in_advance_for_reserve: number;
}

export interface NewTeacher {
  external_id: string;
  email: string;
  phone_number: string;
  first_name: string;
  last_name: string;
  profile_description: string;
  full_description: string;
  available_days_of_week: string[];
  price_per_hour: number;
  dictated_courses: DictatedCourse[];
  business_hours: BusinessHours;
  image: string;
  configuration: Configuration;
  teacher_profiles: Record<
    string,
    { profile_description: string; full_description: string; price_per_hour: number }
  >;
}

export const fetchInstitutions = async (type?: InstitutionType) => {
  const url = new URL(`${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/institutions`);

  if (type) {
    url.searchParams.set('type', type);
  }

  const response = await fetch(url.toString());
  const data = await response.json();

  return data as Institution[];
};

export const fetchCourses = async (type?: InstitutionType) => {
  const url = new URL(`${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/courses`);

  if (type) {
    url.searchParams.set('type', type);
  }

  const response = await fetch(url.toString());
  const data = await response.json();

  return data as Course[];
};

export const createTeacher = (newTeacher: NewTeacher): Promise<NewTeacher> => {
  const myHeaders = new Headers();

  myHeaders.append('x-api-key', process.env.REACT_APP_ESTUDI_TEACHERS_ADMIN_API_KEY!);
  myHeaders.append('Content-Type', 'application/json');

  const raw = JSON.stringify(newTeacher);
  const requestOptions = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
    redirect: 'follow'
  } as RequestInit;

  return fetch(`${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/teachers`, requestOptions).then(
    (response) => {
      if (response.status === 201) {
        return response.json();
      }

      return Promise.reject();
    }
  );
};

export interface UpdateTeacherTelegramRequest {
  configurations: {
    telegram_id: number;
  };
}

export const updateTeacherTelegram = (
  teacherExternalId: string,
  telegramId: number
): Promise<UpdateTeacherTelegramRequest> => {
  const myHeaders = new Headers();

  myHeaders.append('x-api-key', process.env.REACT_APP_ESTUDI_TEACHERS_ADMIN_API_KEY!);
  myHeaders.append('Content-Type', 'application/json');

  const body: UpdateTeacherTelegramRequest = {
    configurations: {
      telegram_id: telegramId
    }
  };

  const raw = JSON.stringify(body);
  const requestOptions = {
    method: 'PUT',
    headers: myHeaders,
    body: raw,
    redirect: 'follow'
  } as RequestInit;

  return fetch(
    `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/teachers/${teacherExternalId}`,
    requestOptions
  ).then((response) => {
    if (response.status === 200) {
      return response.json();
    }

    if (response.status === 204) {
      return body;
    }

    return Promise.reject();
  });
};

export interface Teacher {
  external_id: string;
  first_name: string;
  last_name: string;
  image_url: string;
  status: string;
}

export const fetchTeachers = async (): Promise<Teacher[]> => {
  const response = await fetch(`${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/teachers`);

  if (!response.ok) {
    return Promise.reject();
  }

  return response.json() as Promise<Teacher[]>;
};

export const fetchTeacher = async (teacherExternalId: string): Promise<Teacher> => {
  const response = await fetch(
    `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/teachers/${teacherExternalId}`
  );

  if (!response.ok) {
    return Promise.reject();
  }

  return response.json() as Promise<Teacher>;
};

export const updateTeacherImage = (
  teacherExternalId: string,
  imageBase64: string,
  accessToken: string
): Promise<void> => {
  const myHeaders = new Headers();

  myHeaders.append('Authorization', `Bearer ${accessToken}`);
  myHeaders.append('Content-Type', 'application/json');

  const requestOptions = {
    method: 'PUT',
    headers: myHeaders,
    body: JSON.stringify({ image: imageBase64 }),
    redirect: 'follow'
  } as RequestInit;

  return fetch(
    `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/teachers/${teacherExternalId}`,
    requestOptions
  ).then((response) => {
    if (response.status === 204 || response.status === 200) {
      return;
    }

    return Promise.reject();
  });
};
